<div class="eoi-page" [style.--eoi-primary]="brandBg" [style.--eoi-on-primary]="brandText" [style.--eoi-accent]="brandAccent">
  <!--
    Brand strip reused by the passcode gate and every terminal screen: project logo/name only, at
    the top — matching the banner on the form itself (`.eoi-banner__brand` below). The office
    (YPM) mark closes out the bottom of these cards instead via `officeBar`, the same top/bottom
    split the form uses between its banner and its footer, so branding reads the same everywhere
    rather than doubling up both marks into one strip only on these screens.
  -->
  <ng-template #brandBar>
    <div class="eoi-brandbar" *ngIf="projectLogo || projectName">
      <div class="eoi-brandbar__mark">
        <img class="eoi-brandbar__logo" *ngIf="projectLogo" [src]="projectLogo" [alt]="projectName || 'Project'" />
        <span class="eoi-brandbar__name" *ngIf="!projectLogo && projectName">{{ projectName }}</span>
      </div>
    </div>
  </ng-template>

  <!-- Office (YPM) mark — the bottom counterpart to `brandBar`, matching `.eoi-footer` on the form. -->
  <ng-template #officeBar>
    <div class="eoi-brandbar eoi-brandbar--foot">
      <div class="eoi-brandbar__mark">
        <img class="eoi-brandbar__logo" *ngIf="officeLogo" [src]="officeLogo" [alt]="branding?.office_name || 'YPM Group'" />
        <div class="eoi-banner__wordmark" *ngIf="!officeLogo">YPM<span>GROUP</span></div>
      </div>
    </div>
  </ng-template>

  <!-- ===== Loading ===== -->
  <!-- `eoi-card` is what gives this a bounded width, white surface and vertical centering — without
       it the div has no size of its own, inherits the page's flex `stretch`, and grows into a
       full-height sliver instead of a card. -->
  <div class="eoi-card eoi-status" *ngIf="state === 'loading'">
    <div class="eoi-spinner"></div>
    <p>Loading your form…</p>
  </div>

  <!-- ===== Terminal states ===== -->
  <!--
    `not_found`, `expired` and `cancelled` deliberately share one neutral message. Which of the
    three it is would tell an unauthenticated visitor whether a given token exists, and that is
    not theirs to learn.
  -->
  <div class="eoi-card eoi-status" *ngIf="state === 'not_found' || state === 'expired' || state === 'cancelled'">
    <ng-container *ngTemplateOutlet="brandBar"></ng-container>
    <i class="fa-solid fa-ban"></i>
    <h2>Link no longer available.</h2>
    <p>This Expression of Interest link is no longer active.<br />If you believe this is an error, please contact your agent for assistance.</p>
    <ng-container *ngTemplateOutlet="officeBar"></ng-container>
  </div>
  <div class="eoi-card eoi-status" *ngIf="state === 'already_completed'">
    <ng-container *ngTemplateOutlet="brandBar"></ng-container>
    <i class="fa-solid fa-circle-check"></i>
    <h2>Already submitted</h2>
    <p>This Expression of Interest has already been completed.<br />Contact your agent if you need to make changes.</p>
    <ng-container *ngTemplateOutlet="officeBar"></ng-container>
  </div>

  <!-- ===== One-time passcode gate ===== -->
  <div class="eoi-card eoi-otp" *ngIf="state === 'otp'">
    <ng-container *ngTemplateOutlet="brandBar"></ng-container>
    <div class="eoi-otp__icon"><i class="fa-solid fa-shield-halved"></i></div>
    <h2>Verify it's you</h2>

    <ng-container *ngIf="!otpSent && !otpUnavailable">
      <!-- "one-time" carries a non-breaking hyphen so it never splits over
           two lines. -->
      <p>
        This form contains personal information. To protect your privacy, we'll email you a one&#8209;time passcode before you can access it.
        <span *ngIf="otpSentTo"
          ><br />We'll send it to <strong>{{ otpSentTo }}</strong
          >.</span
        >
      </p>
      <button type="button" class="eoi-submit" (click)="sendOtp()" [disabled]="otpSending">
        {{ otpSending ? 'Sending…' : 'Email Password' }}
      </button>
    </ng-container>

    <!-- No email on the link: a retry button here would never succeed, so the
         buyer is told what is wrong and who can fix it. -->
    <div class="eoi-otp__blocked" *ngIf="otpUnavailable">
      <i class="fa-solid fa-envelope-open-text"></i>
      <h3>No email address on file</h3>
      <p>{{ otpError }}</p>
    </div>

    <ng-container *ngIf="otpSent">
      <p>
        We've emailed a passcode<span *ngIf="otpSentTo">
          to <strong>{{ otpSentTo }}</strong></span
        >.<br />Enter it below to continue.
      </p>
      <input
        class="eoi-otp__input"
        type="text"
        inputmode="numeric"
        autocomplete="one-time-code"
        placeholder="Enter passcode"
        [(ngModel)]="otpCode"
        (keyup.enter)="verifyOtp()" />
      <button type="button" class="eoi-submit" (click)="verifyOtp()" [disabled]="otpVerifying">
        {{ otpVerifying ? 'Verifying…' : 'Continue' }}
      </button>
      <button type="button" class="eoi-otp__resend" (click)="sendOtp()" [disabled]="otpSending">
        {{ otpSending ? 'Sending…' : "Didn't get it? Send again" }}
      </button>
    </ng-container>

    <!-- Suppressed while blocked: the message is already shown above. -->
    <p class="eoi-otp__error" *ngIf="otpError && !otpUnavailable">{{ otpError }}</p>

    <ng-container *ngTemplateOutlet="officeBar"></ng-container>
  </div>

  <!-- ===== Success ===== -->
  <div class="eoi-card eoi-success" *ngIf="state === 'submitted'">
    <ng-container *ngTemplateOutlet="brandBar"></ng-container>
    <div class="eoi-success__icon"><i class="fa-solid fa-circle-check"></i></div>
    <h2>{{ wording.success_title }}</h2>
    <p>{{ wording.success_text }}</p>
    <ng-container *ngTemplateOutlet="officeBar"></ng-container>
  </div>

  <!-- ===== Form ===== -->
  <div class="eoi-card" *ngIf="state === 'ready'">
    <!-- Banner: slide-show photo behind a brand-coloured scrim -->
    <header class="eoi-banner" [class.eoi-banner--photo]="!!branding?.banner_image" [style.background-image]="bannerStyle">
      <!-- Project logo leads the banner, matching the brand strip on the
           passcode and terminal screens — the office (YPM) mark closes out
           the footer below instead of sharing this line. -->
      <div class="eoi-banner__brand">
        <img *ngIf="projectLogo" [src]="projectLogo" [alt]="projectName || 'Project'" />
      </div>
      <!-- Project name only — no address here. The real live form never shows one in the banner;
           this used to render one anyway whenever `property.location` was truthy, but that field
           is only ever populated for the Property Details card's own "Location" row, not for this
           heading, and preview always fetches a real address for that row regardless of whether
           "Location" is one of the agent's selected fields. -->
      <div class="eoi-banner__property" *ngIf="property">
        <strong class="eoi-banner__project-name">{{ property.project_name }}</strong>
      </div>
    </header>

    <div class="eoi-body">
      <h1 class="eoi-title">{{ wording.form_title }}</h1>
      <p class="eoi-intro">{{ wording.form_intro }}</p>

      <!-- The deposit has not been received, so nothing here holds the lot. Wording is the
           project's own (Form Settings → Page wording), falling back to the standard copy. -->
      <div class="eoi-notice" *ngIf="showsNoReservationWarning">
        <i class="fa-solid fa-circle-exclamation"></i>
        <div>
          <strong>{{ wording.notice_title }}</strong>
          <p>{{ wording.notice_text }}</p>
        </div>
      </div>

      <!-- Property details card — driven by the property fields the agent selected -->
      <!--
        Specs render as label/value rows rather than the old wrapping tile strip. Tiles were sized
        by their content, so a row could hold six narrow ones and the next only two, and the eye
        had no column to follow. Rows keep the agent's configured order readable top to bottom.
        (The tile markup used to render a second time directly below this block, as a leftover
        from that refactor — it duplicated every spec and was most of why this card felt oversized.)
      -->
      <section class="eoi-property" *ngIf="showPropertyCard">
        <h3>
          {{ sectionLabel('property', 'Property Details') }}
          <!-- Builder Preview only. Its figures are invented, so this card is always full here
               while a real link shows only what its lot has recorded — without saying so, the two
               side by side read as "the generated link is missing everything". -->
          <span class="eoi-property__sample" *ngIf="sampleProperty">Sample data — a real link shows this lot's own values</span>
        </h3>
        <!-- The identity row is always four across on desktop — Project · Lot · Location · Status,
             the four meta keys, on one line. Fixed rather than following `property_columns`: that
             setting governs the spec rows below, where the number of fields actually varies. -->
        <div class="eoi-property__meta" *ngIf="propertyMetaFields.length">
          <!-- "—" rather than dropping the row: a field the agent selected stays visible even when
               the lot has no value recorded for it — see `propertyMetaFields`. -->
          <div class="eoi-property__meta-item" *ngFor="let f of propertyMetaFields">
            <span class="lbl">{{ metaLabel(f) }}</span>
            <strong>{{ infoValue(f) || '—' }}</strong>
          </div>
        </div>
        <!-- Every spec row in ONE grid, dollar figures first — see `propertySpecRows`. Splitting
             prices into a `dl` of their own restarted the grid at column one, leaving empty cells
             mid-card whenever the price block didn't fill its last row. -->
        <dl class="eoi-property__specs eoi-property__specs--grid" [ngClass]="propertyColumnsClass" *ngIf="propertySpecRows.length">
          <div class="eoi-property__spec" *ngFor="let f of propertySpecRows">
            <dt>
              <i *ngIf="specIcon(f)" [class]="specIcon(f)" aria-hidden="true"></i>
              {{ f.label }}
            </dt>
            <dd [class.is-empty]="!infoValue(f)">{{ infoValue(f) || '—' }}</dd>
          </div>
        </dl>
      </section>

      <!-- Form sections -->
      <form [formGroup]="form" (ngSubmit)="submit()" novalidate>
        <!--
          Shared control markup for one answerable field (everything except info/static_text/divider/
          columns, which aren't part of the reactive form) — declared once, invoked via
          `ngTemplateOutlet` both from the normal per-section loop below AND recursively (one level)
          for each field nested inside a `columns` container's slots, so the two never diverge into
          separate copies of the same type-chain.

          Declared here, inside the form element (`formGroup` above), not just anywhere convenient:
          `formControlName` resolves its `ControlContainer` from where this template is *declared*,
          not from where `ngTemplateOutlet` later renders it — moved here after that exact mismatch
          threw NG01050 ("formControlName must be used with a parent formGroup directive") with this
          template sitting above the form element instead.
        -->
        <ng-template #fieldControl let-f="f">
          <label [for]="f.key" *ngIf="f.showLabel !== false">
            {{ f.label }}
            <span class="req" *ngIf="f.required">*</span>
          </label>

          <!-- textarea -->
          <textarea
            *ngIf="f.type === 'textarea'"
            [id]="f.key"
            [formControlName]="f.key"
            [placeholder]="f.placeholder || ''"
            rows="4"
            [class.invalid]="isInvalid(f)"></textarea>

          <!-- select -->
          <select *ngIf="f.type === 'select'" [id]="f.key" [formControlName]="f.key" [class.invalid]="isInvalid(f)">
            <option value="">{{ f.placeholder || 'Select an option' }}</option>
            <option *ngFor="let opt of f.options" [value]="opt">{{ opt }}</option>
          </select>

          <!-- checkbox group — answer is an array of the ticked options -->
          <div class="eoi-option-group" *ngIf="f.type === 'checkbox'" [class.invalid]="isInvalid(f)">
            <label class="eoi-option" *ngFor="let opt of f.options; let oi = index" [for]="f.key + '_' + oi">
              <input
                type="checkbox"
                [id]="f.key + '_' + oi"
                [checked]="isChecked(f, opt)"
                (change)="toggleCheckboxOption(f, opt, $event.target.checked)" />
              <span>{{ opt }}</span>
            </label>
          </div>

          <!-- radio group — single-select from options, shares one formControlName -->
          <div class="eoi-option-group" *ngIf="f.type === 'radio'" [class.invalid]="isInvalid(f)">
            <label class="eoi-option" *ngFor="let opt of f.options; let oi = index" [for]="f.key + '_' + oi">
              <input type="radio" [id]="f.key + '_' + oi" [formControlName]="f.key" [value]="opt" />
              <span>{{ opt }}</span>
            </label>
          </div>

          <!-- file -->
          <div class="eoi-upload" *ngIf="f.type === 'file'" [class.invalid]="isInvalid(f)">
            <input #fileInput type="file" [id]="f.key" [accept]="f.fileConfig?.accept || ''" (change)="onFileChange($event, f)" hidden />
            <label [for]="f.key" class="eoi-upload__drop">
              <i
                class="fa-solid"
                [class.fa-cloud-arrow-up]="!preparingFile[f.key]"
                [class.fa-spinner]="preparingFile[f.key]"
                [class.fa-spin]="preparingFile[f.key]"></i>
              <span *ngIf="preparingFile[f.key]">Preparing file…</span>
              <span *ngIf="!preparingFile[f.key] && !fileNames[f.key]">Click to upload or drag and drop</span>
              <span *ngIf="!preparingFile[f.key] && fileNames[f.key]" class="eoi-upload__file">
                <i class="fa-solid fa-file"></i> {{ fileNames[f.key] }}
                <button
                  type="button"
                  class="eoi-upload__remove"
                  (click)="removeFile($event, f, fileInput)"
                  title="Remove file"
                  aria-label="Remove file">
                  <i class="fa-solid fa-xmark"></i>
                </button>
              </span>
              <small *ngIf="f.fileConfig">{{ f.fileConfig.accept }} (Max. {{ f.fileConfig.maxSizeMb }}MB)</small>
            </label>
          </div>

          <!-- default input -->
          <input
            *ngIf="['text', 'email', 'tel', 'number', 'date', 'address'].includes(f.type)"
            [id]="f.key"
            [type]="f.type === 'number' ? 'number' : f.type === 'email' ? 'email' : f.type === 'tel' ? 'tel' : f.type === 'date' ? 'date' : 'text'"
            [formControlName]="f.key"
            [placeholder]="f.placeholder || ''"
            [class.invalid]="isInvalid(f)" />

          <small class="eoi-help" *ngIf="f.helpText">{{ f.helpText }}</small>
          <small class="eoi-error" *ngIf="isInvalid(f)">
            {{ f.type === 'email' ? 'Please enter a valid email address.' : 'This field is required.' }}
          </small>
        </ng-template>

        <!-- Runs of consecutive same-section fields, in the agent's own order — the exact grouping
             the builder canvas draws (`buildExternalEoiSectionRuns`), so a field dragged below
             another section's fields lands under a repeated heading here too rather than being
             pulled back up into one merged section. -->
        <ng-container *ngFor="let run of sectionRuns">
          <section class="eoi-section" *ngIf="runHasFields(run)">
            <h3>{{ sectionLabel(run.section, defaultSectionLabel(run.section)) }}</h3>
            <div class="eoi-grid">
              <!--
                Each field's row width (Full / Half / One Third) is configured in the builder's
                Field Settings panel — `fieldWidthClass` resolves the same default a field with no
                explicit width has always rendered at (full for read-only info/free-text, half for
                everything else), so an unconfigured project's layout is unchanged. A `columns`
                container is always full width — its own slots are the layout choice instead.
              -->
              <div class="eoi-field" *ngFor="let f of run.fields" [ngClass]="fieldWidthClass(f)">
                <!-- Info (read-only) -->
                <ng-container *ngIf="f.type === 'info'">
                  <label *ngIf="f.showLabel !== false">{{ f.label }}</label>
                  <div class="eoi-info">{{ infoValue(f) || '—' }}</div>
                </ng-container>

                <!-- Free text — the agent's own copy, unrelated to any field/value. -->
                <p class="eoi-static-text" *ngIf="f.type === 'static_text'">{{ f.content }}</p>

                <!-- Plain visual rule — no label, no answer, not part of the form group. -->
                <hr class="eoi-divider" *ngIf="f.type === 'divider'" />

                <!-- Columns — a freeform group of fields, independent of any section; each slot's
                     children reuse the exact same control markup via `fieldControl` above. -->
                <div class="eoi-columns" *ngIf="f.type === 'columns'" [style.grid-template-columns]="'repeat(' + f.columnCount + ', 1fr)'">
                  <div class="eoi-columns__slot" *ngFor="let slot of f.columns">
                    <!-- `eoi-field` (not just `eoi-columns__item`) so a slot's children reuse the
                         label/input styling already defined for it above, rather than duplicating
                         it — its `.w-*` width class is a harmless no-op here (a slot is a flex
                         column, not a grid). -->
                    <div class="eoi-field eoi-columns__item" *ngFor="let child of slot">
                      <ng-container *ngTemplateOutlet="fieldControl; context: { f: child }"></ng-container>
                    </div>
                  </div>
                </div>

                <ng-container *ngIf="f.type !== 'info' && f.type !== 'static_text' && f.type !== 'divider' && f.type !== 'columns'">
                  <ng-container *ngTemplateOutlet="fieldControl; context: { f: f }"></ng-container>
                </ng-container>
              </div>
            </div>
          </section>
        </ng-container>

        <!-- Security notice — shown in preview too now that its wording is editable (Form Settings →
             Page wording), so an agent can check what they wrote where it actually appears. -->
        <div class="eoi-secure">
          <i class="fa-solid fa-lock"></i>
          <div>
            <strong>{{ wording.privacy_title }}</strong>
            <p>{{ wording.privacy_text }}</p>
          </div>
        </div>

        <!-- Agent-only ("View Link" from inside the app): keeps whatever the agent has typed as the
             link's draft answers, so the buyer opens a form that's already partly filled in. Kept
             apart from Submit — the agent is saving a head start, not submitting for the buyer. -->
        <div class="eoi-agent-save" *ngIf="canSaveDraft">
          <div class="eoi-agent-save__text">
            <strong>Filling this in for the buyer?</strong>
            <span>Save what you've entered and it will already be filled in when they open their link.</span>
          </div>
          <div class="eoi-agent-save__actions">
            <!-- Downloads exactly what's on screen right now, saved or not — so an agent can grab a
                 copy before sending the link to their client without saving first. -->
            <button
              type="button"
              class="eoi-agent-save__btn eoi-agent-save__btn--secondary"
              (click)="downloadPdf()"
              title="Download a PDF of what's filled in so far">
              <i class="fa-solid fa-file-pdf"></i>
              Download PDF
            </button>
            <button type="button" class="eoi-agent-save__btn" (click)="saveDraft()" [disabled]="savingDraft">
              <i class="fa-solid" [class.fa-floppy-disk]="!savingDraft" [class.fa-spinner]="savingDraft" [class.fa-spin]="savingDraft"></i>
              {{ savingDraft ? 'Saving…' : 'Save for buyer' }}
            </button>
          </div>
        </div>

        <!-- Rendered identically in preview so the agent sees the real button, but inert: there is
             no link behind a preview to submit against. -->
        <button type="submit" class="eoi-submit" [disabled]="submitting || isPreview">
          <i class="fa-solid fa-lock me-1"></i>
          {{ submitting ? 'Submitting…' : wording.submit_label }}
        </button>
      </form>
    </div>

    <!-- Footer -->
    <footer class="eoi-footer">
      <!-- The listing agent's sign-off, once the backend sends one (branding.agent_name) — see
           docs/backend/external-eoi-round6.md §2. Silent no-op until then. -->
      <div class="eoi-footer__agent" *ngIf="branding?.agent_name">
        <i class="fa-solid fa-user-tie"></i>
        <div>
          <strong>{{ branding.agent_name }}</strong>
          <!-- Mobile and office number are separate lines: a buyer ringing the wrong one is
               exactly what "please include the mobile number too" was about. -->
          <span *ngIf="agentMobile">{{ agentMobile }}</span>
          <span *ngIf="agentPhone">{{ agentPhone }}</span>
          <span *ngIf="branding?.agent_email">{{ branding.agent_email }}</span>
        </div>
      </div>
      <!-- Office (YPM) brand only: the project logo already leads the banner
           above, so the footer is where the agency signs off instead. -->
      <div class="eoi-footer__brand">
        <img *ngIf="officeLogo" [src]="officeLogo" [alt]="branding?.office_name || 'YPM Group'" />
        <div class="eoi-banner__wordmark" *ngIf="!officeLogo">YPM<span>GROUP</span></div>
      </div>
      <!-- Privacy Policy / Terms of Use / Contact Us removed: they were dead `javascript:void(0)`
           anchors with nowhere to go. -->
      <div class="eoi-footer__copy">© {{ copyrightYear }} {{ branding?.office_name || 'YPM Group' }}. All rights reserved.</div>
    </footer>
  </div>
</div>
